import { useMemo, useState } from "react";
import { InfoOutlined } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography as T,
  useTheme,
  Button,
  IconButton,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridSortModel,
} from "@mui/x-data-grid";
import useCollaborators from "datasets/hooks/useCollaborators";
import useAccessLogs from "datasets/pages/settings/hooks/useAccessLogs";
import usePermissionLogs from "datasets/pages/settings/hooks/usePermissionLogs";
import { formatDateTime } from "~/utils/date";
import { trackEvent } from "~/utils/monitoring";

const AccessLevelChip = ({
  accessLevel,
  source,
  groupName,
}: {
  accessLevel: string;
  source?: string;
  groupName?: string;
}) => {
  const theme = useTheme();

  const getChipStyles = () => {
    switch (accessLevel) {
      case "owner":
        return {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          secondaryColor: theme.palette.primary.contrastText,
        };
      case "analyze":
        return {
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.grey[700],
          secondaryColor: theme.palette.grey[500],
        };
      default:
        return {
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.grey[700],
          secondaryColor: theme.palette.grey[500],
        };
    }
  };

  const styles = getChipStyles();

  const getTooltipText = () => {
    if (source === "group" && groupName) {
      return `${
        accessLevel.charAt(0).toUpperCase() + accessLevel.slice(1)
      } access granted via "${groupName}" group membership`;
    } else if (source === "group") {
      return `${
        accessLevel.charAt(0).toUpperCase() + accessLevel.slice(1)
      } access granted via group membership`;
    } else {
      return `${
        accessLevel.charAt(0).toUpperCase() + accessLevel.slice(1)
      } access granted directly`;
    }
  };

  const chipContent = (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        letterSpacing: "0.04em",
        fontWeight: 600,
        height: "22px",
        borderRadius: "22px",
        px: 1,
        textTransform: "capitalize",
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        whiteSpace: "nowrap",
      }}
    >
      {accessLevel}
    </Box>
  );

  return (
    <Tooltip title={getTooltipText()} arrow placement="top" enterDelay={300} leaveDelay={200}>
      {chipContent}
    </Tooltip>
  );
};

const CustomToolbar = ({
  userCounts,
  activeFilter,
  onFilterChange,
}: {
  userCounts: { totalUsers?: number; ownersCount?: number; analyzeCount?: number };
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
}) => {
  const theme = useTheme();

  const handleFilterClick = (filter: string) => {
    if (activeFilter === filter) {
      onFilterChange(null);
    } else {
      onFilterChange(filter);
    }
  };

  const CountBox = ({ count, isActive }: { count?: number; isActive?: boolean }) => (
    <Box
      sx={{
        backgroundColor: isActive ? theme.palette.primary.main : "grey.200",
        color: isActive ? theme.palette.primary.contrastText : "text.primary",
        borderRadius: "4px",
        minWidth: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingX: 0.5,
      }}
    >
      <T
        variant="caption"
        fontWeight={500}
        color={isActive ? theme.palette.primary.contrastText : "text.primary"}
      >
        {count ?? 0}
      </T>
    </Box>
  );

  const getButtonStyles = (isActive: boolean) => ({
    height: "100%",
    borderColor: isActive ? theme.palette.primary.main : "grey.300",
    color: isActive ? theme.palette.primary.main : "text.primary",
    "&:hover": {
      borderColor: isActive ? theme.palette.primary.main : "grey.500",
    },
  });

  return (
    <GridToolbarContainer sx={{ p: 0 }}>
      <Box sx={{ alignItems: "stretch", display: "flex", gap: 1, height: 36 }}>
        <GridToolbarQuickFilter
          placeholder="Search users..."
          debounceMs={500}
          variant="outlined"
          size="small"
          sx={{
            height: "fit-content",
            paddingBottom: 0,
            "& .MuiInputBase-input": { py: 1 },
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.300",
              borderRadius: "8px",
              p: 0,
            },
          }}
        />
        <Button
          variant="outlined"
          size="small"
          sx={getButtonStyles(activeFilter === "all")}
          endIcon={<CountBox count={userCounts.totalUsers} isActive={activeFilter === "all"} />}
          onClick={() => handleFilterClick("all")}
        >
          All Users
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={getButtonStyles(activeFilter === "owner")}
          endIcon={<CountBox count={userCounts.ownersCount} isActive={activeFilter === "owner"} />}
          onClick={() => handleFilterClick("owner")}
        >
          Owners
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={getButtonStyles(activeFilter === "analyze")}
          endIcon={
            <CountBox count={userCounts.analyzeCount} isActive={activeFilter === "analyze"} />
          }
          onClick={() => handleFilterClick("analyze")}
        >
          Analyze
        </Button>
      </Box>
    </GridToolbarContainer>
  );
};

const UserAccess = () => {
  const theme = useTheme();
  const [accessLevelFilter, setAccessLevelFilter] = useState<string | null>(null);
  const { data: datasetUsers, isLoading: datasetUsersLoading } = useCollaborators();
  const { data: accessLogs, isLoading: accessLogsLoading } = useAccessLogs();
  const { data: permissionLogs, isLoading: permissionLogsLoading } = usePermissionLogs();

  const handleFilterChange = (filter: string | null) => {
    setAccessLevelFilter(filter);
    trackEvent("dataset_settings_access_filter_changed", {
      filter: filter || "all",
      previous_filter: accessLevelFilter || "all",
    });
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (newSortModel.length === 0) {
      // Sort was disabled/cleared
      trackEvent("dataset_settings_access_sort_disabled");
    } else {
      // Sort was enabled or changed
      const sortModel = newSortModel[0];
      trackEvent("dataset_settings_access_sort_changed", {
        field: sortModel.field,
        sort_direction: sortModel.sort,
      });
    }
  };

  // Calculate user counts and prepare table data from datasetUsers
  const { userCounts, tableData } = useMemo(() => {
    if (!datasetUsers) {
      return {
        userCounts: {
          totalUsers: undefined,
          ownersCount: undefined,
          analyzeCount: undefined,
        },
        tableData: [],
      };
    }

    // Define access level hierarchy (higher number = higher priority)
    const accessLevelPriority: Record<string, number> = {
      preview: 1,
      analyze: 2,
      owner: 3,
    };

    // Combine direct users and users from groups
    const directUsers = datasetUsers.users || [];
    const groupUsers =
      datasetUsers.groups?.flatMap((group) =>
        (group.users || []).map((user) => ({
          ...user,
          source: "group",
          groupName: group.name,
        }))
      ) || [];

    // Combine and deduplicate users by email, keeping the highest access level
    const allUsersMap = new Map();

    // Process all users (direct and from groups)
    [...directUsers.map((user) => ({ ...user, source: "direct" })), ...groupUsers].forEach(
      (user) => {
        const existingUser = allUsersMap.get(user.email);

        if (!existingUser) {
          // First time seeing this user
          allUsersMap.set(user.email, user);
        } else {
          // User already exists, compare access levels
          const existingPriority = accessLevelPriority[existingUser.access_level as string] || 0;
          const newPriority = accessLevelPriority[user.access_level as string] || 0;

          if (newPriority > existingPriority) {
            // New access level is higher, update the user
            allUsersMap.set(user.email, user);
          } else if (newPriority === existingPriority && user.source === "direct") {
            // Same access level but this one is direct access, prefer it
            allUsersMap.set(user.email, user);
          }
        }
      }
    );

    const allUsers = Array.from(allUsersMap.values());

    const totalUsers = allUsers.length;
    const ownersCount = allUsers.filter((user) => user.access_level === "owner").length;
    const analyzeCount = allUsers.filter((user) => user.access_level === "analyze").length;

    // Create table data with access logs and permission logs information
    const tableData = allUsers
      .map((user) => {
        const userAccessLog = accessLogs?.find((log) => log.actor === user.email);
        const userPermissionLog = permissionLogs?.find((log) => log.actor === user.email);
        return {
          id: user.email, // Required for DataGrid
          email: user.email,
          name: user.full_name,
          nameEmail: `${user.full_name} ${user.email}`.trim(), // Combined field for search
          access_level: user.access_level,
          source: user.source,
          groupName: user.groupName,
          lastAccess: userAccessLog?.last_access_date,
          authorizer: userPermissionLog?.authorizer,
          dateGranted: userPermissionLog?.date_granted,
        };
      })
      .sort((a, b) => {
        // Sort by name first, then by email
        const nameA = a.name?.toLowerCase() || a.email.toLowerCase();
        const nameB = b.name?.toLowerCase() || b.email.toLowerCase();
        return nameA.localeCompare(nameB);
      });

    return {
      userCounts: {
        totalUsers,
        ownersCount,
        analyzeCount,
      },
      tableData,
    };
  }, [datasetUsers, accessLogs, permissionLogs]);

  // Filter table data based on active filter
  const filteredTableData = useMemo(() => {
    if (!accessLevelFilter || accessLevelFilter === "all") {
      return tableData;
    }
    return tableData.filter((user) => user.access_level === accessLevelFilter);
  }, [tableData, accessLevelFilter]);

  const columns: GridColDef[] = [
    {
      field: "nameEmail",
      headerName: "Name",
      minWidth: 250,
      flex: 1,
      renderCell: (params) => (
        <Stack gap={0.5}>
          <T variant="body2" fontWeight={500} lineHeight={1.2}>
            {params.row.name || params.row.email}
          </T>
          <T variant="caption" color="grey.500" lineHeight={1.2}>
            {params.row.email}
          </T>
        </Stack>
      ),
    },
    {
      field: "access_level",
      headerName: "Access",
      minWidth: 80,
      renderCell: (params) => (
        <AccessLevelChip
          accessLevel={params.value || ""}
          source={params.row.source}
          groupName={params.row.groupName}
        />
      ),
    },
    {
      field: "grant_data",
      headerName: "Granted by",
      sortable: false,
      minWidth: 220,
      flex: 1,
      renderHeader: () => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <span>Granted by</span>
          <Tooltip
            title="Permission data is sourced from CloudWatch logs (last 30 days). Recent changes may take time to appear."
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              <InfoOutlined sx={{ fontSize: 14, color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        if (permissionLogsLoading) {
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <Skeleton variant="text" width="100%" height={20} />
            </Box>
          );
        }
        return (
          <Stack gap={0.5}>
            <T variant="body2" lineHeight={1.2}>
              {params.row.authorizer}
            </T>
            <T variant="caption" color="grey.500" lineHeight={1.2}>
              {params.row.dateGranted ? formatDateTime(params.row.dateGranted) : ""}
            </T>
          </Stack>
        );
      },
    },
    {
      field: "last_access",
      headerName: "Last Access",
      minWidth: 200,
      renderHeader: () => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <span>Last Access</span>
          <Tooltip
            title="Access data is sourced from CloudWatch logs (last 30 days). Recent activity may take time to appear."
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              <InfoOutlined sx={{ fontSize: 14, color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        if (accessLogsLoading) {
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <Skeleton variant="text" width="100%" height={20} />
            </Box>
          );
        }
        return formatDateTime(params.value);
      },
    },
  ];

  return (
    <Stack>
      <Stack gap={3}>
        <Box>
          <T component="h1" fontSize={20} fontWeight={600} lineHeight={1.8}>
            User Access
          </T>
          <T variant="body2" color="grey.500">
            User access permissions, grant history, and dataset usage activity.
          </T>
        </Box>

        {datasetUsersLoading ? (
          <Box
            sx={{
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <T variant="body2" color="text.secondary">
                Loading user data...
              </T>
            </Stack>
          </Box>
        ) : tableData.length > 0 ? (
          <DataGrid
            autoHeight
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            disableMultipleRowSelection
            density="standard"
            columnHeaderHeight={36}
            rows={filteredTableData}
            columns={columns}
            pageSizeOptions={[25, 50, 100]}
            onSortModelChange={handleSortModelChange}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            slots={{
              toolbar: () => (
                <CustomToolbar
                  userCounts={userCounts}
                  activeFilter={accessLevelFilter}
                  onFilterChange={handleFilterChange}
                />
              ),
            }}
            slotProps={{
              pagination: { SelectProps: { native: true } },
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                  debounceMs: 500,
                },
              },
            }}
            sx={{
              border: "none",
              gap: 3,
              overflow: "visible",
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "unset",
              },
              "& .MuiDataGrid-cell": {
                paddingY: 1.5,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-main": {
                borderWidth: 1,
                borderColor: theme.palette.grey[300],
                borderRadius: "8px",
                borderStyle: "solid",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
                justifyContent: "flex-start",
                minHeight: "unset",
                "& .MuiToolbar-root": { minHeight: "unset", paddingLeft: 0 },
              },
            }}
          />
        ) : (
          <Box
            sx={{
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px dashed ${theme.palette.grey[300]}`,
              borderRadius: 1,
              backgroundColor: theme.palette.grey[50],
            }}
          >
            <Stack spacing={1} alignItems="center">
              <T variant="body1" color="text.secondary">
                No users found
              </T>
              <T variant="body2" color="text.secondary">
                No users have been granted access to this dataset
              </T>
            </Stack>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default UserAccess;
